import { computeTooltipPosition, DEFAULT_TOOLTIP_ANCHORS } from "./computeTooltipPosition";

export interface AnchorOffsets {
	/** Vertical nudge from the trigger's top edge. */
	offsetY?: number;
	/** Horizontal nudge from the trigger's left edge. */
	offsetX?: number;
}

/**
 * Anchors a body-portalled tooltip to its trigger, in **viewport** coordinates.
 *
 * The tooltip is portalled out of its clipping ancestors and therefore positioned `fixed`
 * (apps/web/CLAUDE.md §11). Document coordinates — `getBoundingClientRect()` plus
 * `window.scrollY` — would need an `absolute` box instead, and an absolutely positioned child
 * of `document.body` adds its own box to the *document's* scrollable height; html and body do
 * not clip, so a tooltip opened low on the page makes the whole app scrollable (PREC-4114).
 *
 * A fixed box cannot be scrolled into view, so the position is clamped to the viewport: room
 * below keeps today's coordinates, otherwise it flips above the trigger, and when it fits
 * neither side it becomes a `maxHeight` box that scrolls inside itself (PREC-4675).
 *
 * Seven tooltips across the lead grid, the contacts grid and the tracker repeated this same
 * three-line calculation; they now share it, so there is one place where the coordinate space
 * is decided.
 */
export const anchorTooltipToTrigger = (
	trigger: HTMLElement | null,
	tooltip: HTMLElement | null,
	{
		offsetY = DEFAULT_TOOLTIP_ANCHORS.offsetY,
		offsetX = DEFAULT_TOOLTIP_ANCHORS.offsetX,
	}: AnchorOffsets = {}
): void => {
	if (!trigger || !tooltip) {
		return;
	}

	// Measure the natural size: a constraint left over from a previous anchor would otherwise
	// shrink `offsetHeight` and the box would never grow back (PREC-4675).
	tooltip.style.maxHeight = "";
	tooltip.style.overflowY = "";

	const rect = trigger.getBoundingClientRect();
	const { top, left, maxHeight } = computeTooltipPosition({
		triggerRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
		tooltipSize: { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
		viewport: { width: window.innerWidth, height: window.innerHeight },
		anchors: { offsetY, offsetX },
	});

	tooltip.style.top = `${top}px`;
	tooltip.style.left = `${left}px`;

	if (maxHeight !== undefined) {
		tooltip.style.maxHeight = `${maxHeight}px`;
		tooltip.style.overflowY = "auto";
	}
};
